/*
 * Creator Connect card (assets/creator-connect.js)
 * A creator connects their OWN Instagram / Facebook Page, approves a post,
 * sees basic results and can disconnect. Lives inside the Creator Dashboard.
 *
 * Reuses the dashboard's own session: localStorage 'cd_session' (Bearer) and
 * 'cd_creator_id', and the same API base. It never creates or changes a session.
 * It mounts only while the dashboard itself is showing (#cd-dash.active), which
 * the dashboard sets only after the server accepted the session.
 *
 * If the status call fails, or the backend says enabled:false, nothing is rendered.
 *
 * API CONTRACT (same text as the header of meta_connect_api.py; change both together)
 *   All routes: Authorization: Bearer <creator session>; 401 without it.
 *   GET  /status          -> {enabled, connected, meta_name, connected_at, token_expires_at, accounts:[{id,kind,name,username,picture}]}
 *   POST /connect/start   -> {url} + an HttpOnly cookie on ai-api.wowstore.live. MUST be called with
 *                            credentials:'include': the Facebook round-trip only completes in the browser that
 *                            holds this cookie. The popup ends on OUR confirmation page (names the Facebook
 *                            account, the Pages / Instagram accounts and the creator account); nothing is stored
 *                            before the creator presses "Connect these accounts" there.
 *   POST /publish         body {account_id, kind, media_url, media_type, caption, approved:true, idempotency_key}
 *                         -> 202 {job_id, status:'queued'}. ALWAYS asynchronous. The same idempotency_key returns the
 *                            same job and never posts twice, so a retry after a network error re-sends the SAME key.
 *                            The key changes only when the post content changes (or after a definite 'failed').
 *                            409 = the same file is already open / unconfirmed for this account.
 *   GET  /posts/{job_id}  -> {job_id, status, kind, post_id, permalink, error, created_at}
 *                            status: queued | processing | published | failed | unknown | cancelled.
 *                            'unknown' = Meta may have accepted the post. NEVER offer "try again" for it.
 *   GET  /posts           -> {posts:[...published], pending:n, unknown:n, jobs:[open job views]}
 *   POST /disconnect      -> {disconnected:true, revoked:true|false|null, cancelled_jobs:n}
 *                            revoked:false = deleted on our side, but the app is still authorised on Facebook.
 */
(function () {
  'use strict';

  /* ---- feature flag: ONE constant. 'query' = only with ?connect=1, 'always' = every logged-in creator ---- */
  var FLAG_MODE = 'query';
  var FLAG_PARAM = 'connect';

  /* ---- reused from sections/creator-dashboard.liquid ---- */
  var API = 'https://ai-api.wowstore.live/api/creator';
  var SESSION_KEY = 'cd_session';
  var ID_KEY = 'cd_creator_id';
  var DASH_ID = 'cd-dash';
  var DASH_ACTIVE_CLASS = 'active';

  /* ---- connect popup ---- */
  var POPUP_ORIGIN = 'https://ai-api.wowstore.live';
  var OAUTH_HOSTS = ['www.facebook.com', 'facebook.com', 'm.facebook.com', 'ai-api.wowstore.live'];
  var POPUP_NAME = 'wcn_meta_connect';
  var POPUP_POLL_MS = 600;
  var STATUS_POLL_MS = 4000;
  var STATUS_POLL_MAX = 45;

  /* ---- publish jobs (always asynchronous on the server) ---- */
  var JOB_POLL_MS = 4000;
  var JOB_POLL_MAX = 180;          /* 180 x 4 s = 12 minutes */
  var PUBLISH_SEND_TRIES = 3;      /* same idempotency key every time: a re-send can never post twice */
  var PUBLISH_SEND_GAP_MS = 2000;
  var POSTS_REFRESH_MS = 8000;
  var POSTS_REFRESH_MAX = 90;
  var MSG_UNKNOWN = 'We could not confirm this post. Check your account before you post it again. We keep checking and update this list.';
  var MSG_STILL = 'Still processing \u2014 check your account before trying again.';

  var INFO_URL = '/pages/creator-connect';
  var CAPTION_MAX = 2200;
  var DASH = '\u2014';

  function flagOn() {
    if (FLAG_MODE === 'always') return true;
    try {
      return new URLSearchParams(window.location.search).get(FLAG_PARAM) === '1';
    } catch (e) {
      return false;
    }
  }
  if (!flagOn()) return;

  var dash = document.getElementById(DASH_ID);
  if (!dash) return;

  /* ------------------------------------------------------------------ helpers */

  function readSession() {
    try {
      var s = window.localStorage.getItem(SESSION_KEY);
      var id = window.localStorage.getItem(ID_KEY);
      if (s && id) return { session: s, id: id };
    } catch (e) {}
    return null;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = String(text);
    return n;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* returns a normalised https URL, or null. Anything else (http, javascript, data, blob) is refused. */
  function safeHttps(u) {
    if (typeof u !== 'string' || !u) return null;
    try {
      var x = new URL(u);
      return x.protocol === 'https:' ? x.href : null;
    } catch (e) {
      return null;
    }
  }

  function fmtDate(d) {
    if (!d) return DASH;
    var dt = new Date(d);
    if (isNaN(dt.getTime())) return DASH;
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function fmtNum(v) {
    if (v === null || v === undefined || v === '') return DASH;
    var n = Number(v);
    if (!isFinite(n)) return DASH;
    return n.toLocaleString('en-GB');
  }

  function kindLabel(kind) {
    return kind === 'instagram' ? 'Instagram' : 'Facebook Page';
  }

  function shortText(s, max) {
    s = String(s === null || s === undefined ? '' : s).replace(/\s+/g, ' ').trim();
    if (s.length <= max) return s;
    return s.slice(0, max - 1) + '\u2026';
  }

  var uid = 0;
  function nextId(p) {
    uid += 1;
    return 'wcn-' + p + '-' + uid;
  }

  /* ------------------------------------------------------------------ api */

  var epoch = 0; /* bumped on unmount so late responses are dropped */

  function api(method, path, body, timeoutMs, withCookies) {
    var s = readSession();
    if (!s) return Promise.reject(new Error('no-session'));
    var url = API + '/' + encodeURIComponent(s.id) + '/meta' + path;
    var opts = { method: method, headers: { 'Authorization': 'Bearer ' + s.session } };
    /* only connect/start: lets the server set its HttpOnly browser-binding cookie (same site, other origin) */
    if (withCookies) opts.credentials = 'include';
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    var timer = null;
    if (typeof AbortController === 'function') {
      var ac = new AbortController();
      opts.signal = ac.signal;
      timer = setTimeout(function () { ac.abort(); }, timeoutMs || 20000);
    }
    return fetch(url, opts).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (data) {
        if (timer) clearTimeout(timer);
        return { status: r.status, ok: r.ok, data: data };
      });
    }, function (err) {
      if (timer) clearTimeout(timer);
      throw err;
    });
  }

  function newKey() {
    try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (e) {}
    var b = [], i;
    try {
      var a = new Uint8Array(16);
      window.crypto.getRandomValues(a);
      for (i = 0; i < 16; i++) b.push(a[i]);
    } catch (e2) {
      b = [];
      for (i = 0; i < 16; i++) b.push(Math.floor(Math.random() * 256));
    }
    var h = b.map(function (x) { return (x < 16 ? '0' : '') + x.toString(16); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  function serverDetail(res, fallback) {
    var d = res && res.data;
    var msg = d && (typeof d.detail === 'string' ? d.detail : (typeof d.message === 'string' ? d.message : ''));
    return msg ? shortText(msg, 200) : fallback;
  }

  /* ------------------------------------------------------------------ state */

  var root = null, bodyEl = null, liveEl = null, titleEl = null;
  var status = null;
  var popup = null, popupTimer = null, statusTimer = null, statusTries = 0;
  var connecting = false;
  var publishing = false;
  var jobTimer = null, postsTimer = null, postsRefreshes = 0;

  function stopJobTimers() {
    if (jobTimer) { clearInterval(jobTimer); jobTimer = null; }
    if (postsTimer) { clearTimeout(postsTimer); postsTimer = null; }
    postsRefreshes = 0;
  }

  function isExpired(d) {
    if (!d || !d.token_expires_at) return false;
    var t = new Date(d.token_expires_at).getTime();
    return !isNaN(t) && t < Date.now();
  }

  function isLive(d) {
    return !!(d && d.connected && !isExpired(d));
  }

  var lastRenderKey = '';
  function renderKey() {
    var k = '';
    try { k = JSON.stringify(status); } catch (e) { k = String(Date.now()); }
    return k + '|' + (connecting ? '1' : '0');
  }

  function say(msg) {
    if (!liveEl) return;
    liveEl.textContent = '';
    /* a tick later so screen readers announce a repeated message too */
    setTimeout(function () { if (liveEl) liveEl.textContent = msg || ''; }, 30);
  }

  /* ------------------------------------------------------------------ styles */

  function injectStyles() {
    if (document.getElementById('wcn-styles')) return;
    var css = [
      'section.wcn-card { background: var(--cd-card, #fff); border: 1px solid var(--cd-border, #ececf0); border-radius: var(--cd-radius, 16px); padding: 26px; margin-bottom: 22px; box-shadow: var(--cd-shadow, 0 1px 3px rgba(17,24,39,.04)); font-family: var(--cd-sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif); color: var(--cd-text, #374151); box-sizing: border-box; }',
      '.wcn-card *, .wcn-card *::before, .wcn-card *::after { box-sizing: border-box; }',
      '.wcn-card [hidden] { display: none !important; }',
      '.wcn-card .wcn-title { font-size: 17px; font-weight: 800; line-height: 1.3; color: var(--cd-ink, #111827); letter-spacing: -.3px; margin: 0 0 12px; outline: none; }',
      '.wcn-card .wcn-p { font-size: 14px; line-height: 1.55; margin: 0 0 10px; color: var(--cd-text, #374151); }',
      '.wcn-card .wcn-note { font-size: 13px; line-height: 1.55; margin: 0 0 10px; color: var(--cd-muted, #6B7280); }',
      '.wcn-card .wcn-live { font-size: 13px; line-height: 1.5; color: var(--cd-ink, #111827); min-height: 0; margin: 0; }',
      '.wcn-card .wcn-live:not(:empty) { margin: 0 0 14px; padding: 10px 14px; background: var(--cd-soft, #faf9fb); border: 1px solid var(--cd-border-2, #e2e1e8); border-radius: 10px; }',
      '.wcn-card .wcn-actions { display: flex; flex-wrap: wrap; gap: 10px 16px; align-items: center; margin: 14px 0 0; }',
      '.wcn-card .wcn-btn { display: inline-block; padding: 11px 18px; border-radius: 10px; font-family: inherit; font-size: 14px; font-weight: 700; line-height: 1.2; cursor: pointer; border: 1px solid var(--cd-border-2, #e2e1e8); background: var(--cd-soft, #faf9fb); color: var(--cd-text, #374151); max-width: 100%; white-space: normal; text-align: center; }',
      '.wcn-card .wcn-btn-primary { border: none; color: #fff; background: var(--cd-grad, linear-gradient(135deg, #DB2777 0%, #8B5CF6 50%, #6366F1 100%)); }',
      '.wcn-card .wcn-btn-danger { border-color: #FCA5A5; color: #B91C1C; background: #fff; }',
      '.wcn-card .wcn-btn:disabled { opacity: .5; cursor: not-allowed; }',
      '.wcn-card .wcn-link { color: var(--cd-pink, #DB2777); font-size: 14px; font-weight: 600; text-decoration: underline; overflow-wrap: anywhere; }',
      '.wcn-card .wcn-btn:focus-visible, .wcn-card .wcn-link:focus-visible, .wcn-card .wcn-input:focus-visible, .wcn-card .wcn-check:focus-visible, .wcn-card .wcn-title:focus-visible { outline: 3px solid var(--cd-purple, #8B5CF6); outline-offset: 2px; }',
      '.wcn-card .wcn-sub { font-size: 14px; font-weight: 800; color: var(--cd-ink, #111827); margin: 24px 0 12px; padding: 18px 0 0; border-top: 1px solid var(--cd-border, #ececf0); }',
      '.wcn-card .wcn-accounts { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 10px; }',
      '.wcn-card .wcn-account { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid var(--cd-border-2, #e2e1e8); border-radius: 12px; background: var(--cd-soft, #faf9fb); min-width: 0; }',
      '.wcn-card .wcn-pic { width: 40px; height: 40px; border-radius: 50%; flex: 0 0 40px; object-fit: cover; background: var(--cd-purple-soft, rgba(139,92,246,.08)); color: var(--cd-purple, #8B5CF6); display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; }',
      '.wcn-card .wcn-account-text { min-width: 0; flex: 1; }',
      '.wcn-card .wcn-account-name { font-size: 14px; font-weight: 700; color: var(--cd-ink, #111827); overflow-wrap: anywhere; }',
      '.wcn-card .wcn-account-kind { font-size: 12px; color: var(--cd-muted, #6B7280); overflow-wrap: anywhere; }',
      '.wcn-card .wcn-confirm { margin: 14px 0 0; padding: 14px; border: 1px solid #FCA5A5; border-radius: 12px; background: rgba(220,38,38,.04); }',
      '.wcn-card .wcn-field { margin: 0 0 16px; }',
      '.wcn-card .wcn-label { display: block; font-size: 12px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; color: var(--cd-muted, #6B7280); margin: 0 0 6px; }',
      '.wcn-card .wcn-input { display: block; width: 100%; max-width: 100%; padding: 12px 14px; background: var(--cd-soft, #faf9fb); border: 1px solid var(--cd-border-2, #e2e1e8); border-radius: var(--cd-radius-sm, 12px); color: var(--cd-ink, #111827); font-family: inherit; font-size: 16px; line-height: 1.4; }',
      '.wcn-card textarea.wcn-input { resize: vertical; min-height: 110px; }',
      '.wcn-card .wcn-hint { font-size: 12px; line-height: 1.5; color: var(--cd-muted, #6B7280); margin: 6px 0 0; }',
      '.wcn-card .wcn-hint-err { color: #B91C1C; }',
      '.wcn-card .wcn-counter { font-size: 12px; color: var(--cd-muted, #6B7280); margin: 6px 0 0; text-align: right; }',
      '.wcn-card .wcn-preview { border: 1px dashed var(--cd-border-2, #e2e1e8); border-radius: 12px; padding: 14px; margin: 0 0 16px; background: var(--cd-soft, #faf9fb); }',
      '.wcn-card .wcn-preview-head { font-size: 12px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; color: var(--cd-muted, #6B7280); margin: 0 0 8px; }',
      '.wcn-card .wcn-preview-acct { font-size: 13px; font-weight: 700; color: var(--cd-ink, #111827); margin: 0 0 8px; overflow-wrap: anywhere; }',
      '.wcn-card .wcn-preview-media { display: block; max-width: 100%; width: 320px; max-height: 400px; border-radius: 10px; margin: 0 0 10px; background: #000; object-fit: contain; }',
      '.wcn-card .wcn-preview-caption { font-size: 14px; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere; margin: 0; color: var(--cd-text, #374151); }',
      '.wcn-card .wcn-approve { display: flex; align-items: flex-start; gap: 10px; margin: 0 0 6px; font-size: 14px; font-weight: 600; color: var(--cd-ink, #111827); line-height: 1.4; }',
      '.wcn-card .wcn-check { width: 20px; height: 20px; flex: 0 0 20px; margin: 1px 0 0; accent-color: var(--cd-purple, #8B5CF6); }',
      '.wcn-card .wcn-posts { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }',
      '.wcn-card .wcn-post { border: 1px solid var(--cd-border-2, #e2e1e8); border-radius: 12px; padding: 14px; min-width: 0; }',
      '.wcn-card .wcn-post-caption { font-size: 14px; font-weight: 600; color: var(--cd-ink, #111827); margin: 0 0 4px; overflow-wrap: anywhere; }',
      '.wcn-card .wcn-post-meta { font-size: 12px; color: var(--cd-muted, #6B7280); margin: 0 0 10px; overflow-wrap: anywhere; }',
      '.wcn-card .wcn-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin: 0 0 10px; }',
      '.wcn-card .wcn-metric { background: var(--cd-soft, #faf9fb); border-radius: 8px; padding: 8px; text-align: center; margin: 0; }',
      '.wcn-card .wcn-metric dt { font-size: 11px; color: var(--cd-muted, #6B7280); margin: 0 0 2px; }',
      '.wcn-card .wcn-metric dd { font-size: 15px; font-weight: 800; color: var(--cd-ink, #111827); margin: 0; }',
      '@media (max-width: 600px) {',
      '  section.wcn-card { padding: 20px 16px; }',
      '  .wcn-card .wcn-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }',
      '  .wcn-card .wcn-actions .wcn-btn { width: 100%; }',
      '}'
    ].join('\n');
    var st = document.createElement('style');
    st.id = 'wcn-styles';
    st.textContent = css;
    document.head.appendChild(st);
  }

  /* ------------------------------------------------------------------ mount / unmount */

  function buildRoot() {
    injectStyles();
    root = el('section', 'wcn-card');
    var tid = nextId('title');
    root.setAttribute('aria-labelledby', tid);
    titleEl = el('h2', 'wcn-title', 'Post to your own Instagram or Facebook Page');
    titleEl.id = tid;
    titleEl.setAttribute('tabindex', '-1');
    liveEl = el('div', 'wcn-live');
    liveEl.setAttribute('role', 'status');
    liveEl.setAttribute('aria-live', 'polite');
    bodyEl = el('div', 'wcn-body');
    root.appendChild(titleEl);
    root.appendChild(liveEl);
    root.appendChild(bodyEl);
  }

  function attachRoot() {
    if (!root) buildRoot();
    if (!root.parentNode) dash.appendChild(root);
  }

  function detachRoot() {
    if (root && root.parentNode) root.parentNode.removeChild(root);
  }

  function stopTimers() {
    if (popupTimer) { clearInterval(popupTimer); popupTimer = null; }
    if (statusTimer) { clearInterval(statusTimer); statusTimer = null; }
    statusTries = 0;
  }

  function closePopup() {
    try { if (popup && !popup.closed) popup.close(); } catch (e) {}
    popup = null;
  }

  var mounted = false;

  function mount() {
    if (mounted) return;
    mounted = true;
    epoch += 1;
    window.addEventListener('message', onMessage);
    loadStatus(false);
  }

  function unmount() {
    if (!mounted) return;
    mounted = false;
    epoch += 1;
    window.removeEventListener('message', onMessage);
    stopTimers();
    stopJobTimers();
    closePopup();
    connecting = false;
    publishing = false;
    status = null;
    detachRoot();
    root = null; bodyEl = null; liveEl = null; titleEl = null;
    postsBox = null;
    lastRenderKey = '';
  }

  function sync() {
    var want = dash.classList.contains(DASH_ACTIVE_CLASS) && !!readSession();
    if (want) mount(); else unmount();
  }

  /* ------------------------------------------------------------------ status */

  function loadStatus(focusTitle) {
    var my = epoch;
    return api('GET', '/status').then(function (res) {
      if (my !== epoch) return;
      var d = res.data;
      if (!res.ok || !d || d.enabled !== true) {
        /* 401/403/404/5xx or enabled:false: render nothing at all */
        stopTimers();
        connecting = false;
        status = null;
        detachRoot();
        return;
      }
      status = d;
      if (connecting && isLive(d)) {
        stopTimers();
        closePopup();
        connecting = false;
        say('Connected.');
        focusTitle = true;
      }
      attachRoot();
      /* no re-render when nothing changed: keeps focus and any typed text in place while we poll */
      if (renderKey() !== lastRenderKey) render();
      if (focusTitle && titleEl) { try { titleEl.focus(); } catch (e) {} }
    }).catch(function () {
      if (my !== epoch) return;
      if (!status) detachRoot();
    });
  }

  /* ------------------------------------------------------------------ connect */

  function onMessage(ev) {
    if (ev.origin !== POPUP_ORIGIN) return;
    if (!popup || ev.source !== popup) return;
    var d = ev.data;
    if (!d || typeof d !== 'object') return;
    if (d.type === 'wow_meta_connected') {
      stopTimers();
      closePopup();
      connecting = false;
      say('Connected.');
      loadStatus(true);
    } else if (d.type === 'wow_meta_error') {
      stopTimers();
      closePopup();
      connecting = false;
      say('The connection was not completed. You can try again.');
      loadStatus(true);
    }
  }

  function oauthUrlAllowed(u) {
    var safe = safeHttps(u);
    if (!safe) return null;
    var host = new URL(safe).hostname.toLowerCase();
    return OAUTH_HOSTS.indexOf(host) !== -1 ? safe : null;
  }

  function startStatusPolling() {
    if (statusTimer) return;
    statusTries = 0;
    statusTimer = setInterval(function () {
      statusTries += 1;
      if (!mounted || !connecting || statusTries > STATUS_POLL_MAX) {
        if (statusTimer) { clearInterval(statusTimer); statusTimer = null; }
        if (mounted && connecting) {
          connecting = false;
          say('We could not confirm the connection. You can try again.');
          render();
        }
        return;
      }
      loadStatus(false);
    }, STATUS_POLL_MS);
  }

  function startConnect() {
    if (connecting) return;
    /* open the window inside the click, before any await, so the browser keeps the user gesture */
    var w = null;
    try { w = window.open('', POPUP_NAME, 'width=600,height=740'); } catch (e) { w = null; }
    if (!w) {
      say('Your browser blocked the window. Allow pop-ups for this site, then try again.');
      return;
    }
    popup = w;
    try {
      w.document.title = 'Connect';
      w.document.body.textContent = 'Opening Facebook\u2026';
    } catch (e) {}
    connecting = true;
    render();
    say('Finish the steps in the window that opened.');
    var my = epoch;
    api('POST', '/connect/start', null, null, true).then(function (res) {
      if (my !== epoch) return;
      var target = res.ok && res.data ? oauthUrlAllowed(res.data.url) : null;
      if (!target) {
        closePopup();
        connecting = false;
        render();
        say(res.status === 401 ? 'Your login has expired. Log in again.' : 'We could not start the connection. Please try again later.');
        return;
      }
      try { w.location.href = target; } catch (e) {
        closePopup();
        connecting = false;
        render();
        say('We could not open the connection window. Please try again.');
        return;
      }
      popupTimer = setInterval(function () {
        var closed = true;
        try { closed = !popup || popup.closed; } catch (e) { closed = true; }
        if (!closed) return;
        clearInterval(popupTimer); popupTimer = null;
        popup = null;
        if (!mounted || !connecting) return;
        /* the window is gone (or the browser cut our handle to it): ask the server, and keep asking for a short while */
        loadStatus(false);
        startStatusPolling();
      }, POPUP_POLL_MS);
    }).catch(function () {
      if (my !== epoch) return;
      closePopup();
      connecting = false;
      render();
      say('We could not reach the server. Please try again.');
    });
  }

  function cancelConnect() {
    stopTimers();
    closePopup();
    connecting = false;
    render();
    say('Connection cancelled.');
  }

  /* ------------------------------------------------------------------ disconnect */

  function doDisconnect(btn) {
    btn.disabled = true;
    var my = epoch;
    api('POST', '/disconnect').then(function (res) {
      if (my !== epoch) return;
      if (res.ok && res.data && res.data.disconnected === true) {
        stopJobTimers();
        publishing = false;
        say(res.data.revoked === false
          ? 'Disconnected. We deleted our access on our side, but Facebook did not confirm it. To be sure, remove WowStore in your Facebook settings under Apps and websites.'
          : 'Disconnected.');
        loadStatus(true);
      } else {
        btn.disabled = false;
        say(serverDetail(res, 'We could not disconnect. Please try again.'));
      }
    }).catch(function () {
      if (my !== epoch) return;
      btn.disabled = false;
      say('We could not reach the server. Please try again.');
    });
  }

  /* ------------------------------------------------------------------ render */

  function render() {
    if (!bodyEl || !status) return;
    lastRenderKey = renderKey();
    clear(bodyEl);
    if (status.connected) renderConnected(); else renderNotConnected();
  }

  function promiseLines(parent) {
    parent.appendChild(el('p', 'wcn-p', 'Nothing is posted without your approval. You can disconnect at any time.'));
  }

  function infoLink() {
    var a = el('a', 'wcn-link', 'What we can and cannot see');
    a.href = INFO_URL;
    return a;
  }

  function renderNotConnected() {
    bodyEl.appendChild(el('p', 'wcn-p', 'Connect your own Instagram account or Facebook Page to publish a post from this dashboard.'));
    promiseLines(bodyEl);
    bodyEl.appendChild(el('p', 'wcn-note', 'Posting to Instagram needs a professional Instagram account that is linked to a Facebook Page.'));
    var actions = el('div', 'wcn-actions');
    if (connecting) {
      bodyEl.appendChild(el('p', 'wcn-note', 'Waiting for you to finish in the other window. This card updates when you are done.'));
      var check = el('button', 'wcn-btn wcn-btn-primary', 'Check connection');
      check.type = 'button';
      check.addEventListener('click', function () { say('Checking.'); loadStatus(false); });
      var cancel = el('button', 'wcn-btn', 'Cancel');
      cancel.type = 'button';
      cancel.addEventListener('click', cancelConnect);
      actions.appendChild(check);
      actions.appendChild(cancel);
    } else {
      var b = el('button', 'wcn-btn wcn-btn-primary', 'Connect Instagram or Facebook Page');
      b.type = 'button';
      b.addEventListener('click', startConnect);
      actions.appendChild(b);
    }
    actions.appendChild(infoLink());
    bodyEl.appendChild(actions);
  }

  function accountPic(acct) {
    var src = safeHttps(acct.picture);
    var fallback = el('span', 'wcn-pic', (String(acct.name || acct.username || '?').charAt(0) || '?').toUpperCase());
    fallback.setAttribute('aria-hidden', 'true');
    if (!src) return fallback;
    var img = el('img', 'wcn-pic');
    img.alt = '';
    img.width = 40; img.height = 40;
    img.loading = 'lazy';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', function () {
      if (img.parentNode) img.parentNode.replaceChild(fallback, img);
    });
    img.src = src;
    return img;
  }

  function renderConnected() {
    var accounts = Array.isArray(status.accounts) ? status.accounts : [];
    var expired = isExpired(status);

    var who = 'Connected' + (status.meta_name ? ' as ' + status.meta_name : '') + '.';
    if (status.connected_at) who += ' Since ' + fmtDate(status.connected_at) + '.';
    bodyEl.appendChild(el('p', 'wcn-p', who));
    if (status.token_expires_at) {
      bodyEl.appendChild(el('p', 'wcn-note', expired
        ? 'This connection has expired. Connect again to keep posting.'
        : 'This connection is valid until ' + fmtDate(status.token_expires_at) + '.'));
    }
    promiseLines(bodyEl);

    if (accounts.length) {
      var ul = el('ul', 'wcn-accounts');
      ul.setAttribute('aria-label', 'Connected accounts');
      accounts.forEach(function (a) {
        var li = el('li', 'wcn-account');
        li.appendChild(accountPic(a));
        var tx = el('div', 'wcn-account-text');
        tx.appendChild(el('div', 'wcn-account-name', a.name || a.username || 'Account'));
        tx.appendChild(el('div', 'wcn-account-kind', kindLabel(a.kind) + (a.username ? ' \u00b7 @' + a.username : '')));
        li.appendChild(tx);
        ul.appendChild(li);
      });
      bodyEl.appendChild(ul);
    } else {
      bodyEl.appendChild(el('p', 'wcn-note', 'We found no Facebook Page or professional Instagram account on this login. Disconnect, then connect again and select the Page you want to use.'));
    }

    /* actions: reconnect (when expired), disconnect with a confirm step, info link */
    var actions = el('div', 'wcn-actions');
    if (expired) {
      var re = el('button', 'wcn-btn wcn-btn-primary', connecting ? 'Check connection' : 'Connect again');
      re.type = 'button';
      re.addEventListener('click', function () { if (connecting) loadStatus(false); else startConnect(); });
      actions.appendChild(re);
    }
    var dis = el('button', 'wcn-btn', 'Disconnect');
    dis.type = 'button';
    actions.appendChild(dis);
    actions.appendChild(infoLink());
    bodyEl.appendChild(actions);

    var confirmBox = el('div', 'wcn-confirm');
    confirmBox.hidden = true;
    confirmBox.setAttribute('role', 'group');
    var cid = nextId('confirm');
    var cq = el('p', 'wcn-p', 'Disconnect this account? We delete our access. A post that is still being published is stopped. Posts you already published stay where they are.');
    cq.id = cid;
    confirmBox.setAttribute('aria-labelledby', cid);
    confirmBox.appendChild(cq);
    var cActions = el('div', 'wcn-actions');
    var no = el('button', 'wcn-btn', 'Keep connected');
    no.type = 'button';
    var yes = el('button', 'wcn-btn wcn-btn-danger', 'Yes, disconnect');
    yes.type = 'button';
    cActions.appendChild(no);
    cActions.appendChild(yes);
    confirmBox.appendChild(cActions);
    bodyEl.appendChild(confirmBox);

    dis.setAttribute('aria-expanded', 'false');
    dis.addEventListener('click', function () {
      confirmBox.hidden = false;
      dis.setAttribute('aria-expanded', 'true');
      dis.disabled = true;
      no.focus();
    });
    no.addEventListener('click', function () {
      confirmBox.hidden = true;
      dis.disabled = false;
      dis.setAttribute('aria-expanded', 'false');
      dis.focus();
    });
    yes.addEventListener('click', function () { doDisconnect(yes); });

    if (accounts.length && !expired) renderPublishForm(accounts);
    renderPosts();
  }

  /* ------------------------------------------------------------------ publish form */

  function guessMediaType(url) {
    var path = '';
    try { path = new URL(url).pathname.toLowerCase(); } catch (e) { return null; }
    if (/\.(mp4|mov|m4v|webm)$/.test(path)) return 'video';
    if (/\.(jpe?g|png|webp|gif)$/.test(path)) return 'image';
    return null;
  }

  function renderPublishForm(accounts) {
    bodyEl.appendChild(el('h3', 'wcn-sub', 'Create a post'));
    var form = el('form', 'wcn-form');
    form.noValidate = true;

    /* account */
    var fAcct = el('div', 'wcn-field');
    var acctId = nextId('acct');
    var lAcct = el('label', 'wcn-label', 'Account');
    lAcct.htmlFor = acctId;
    var sel = el('select', 'wcn-input');
    sel.id = acctId;
    accounts.forEach(function (a, i) {
      var o = el('option', null, (a.name || a.username || 'Account') + ' \u00b7 ' + kindLabel(a.kind));
      o.value = String(i);
      sel.appendChild(o);
    });
    fAcct.appendChild(lAcct);
    fAcct.appendChild(sel);
    form.appendChild(fAcct);

    /* media url (https only) */
    var fUrl = el('div', 'wcn-field');
    var urlId = nextId('url'), urlHintId = nextId('urlhint');
    var lUrl = el('label', 'wcn-label', 'Media link');
    lUrl.htmlFor = urlId;
    var inUrl = el('input', 'wcn-input');
    inUrl.type = 'url';
    inUrl.id = urlId;
    inUrl.setAttribute('inputmode', 'url');
    inUrl.setAttribute('autocomplete', 'off');
    inUrl.setAttribute('spellcheck', 'false');
    inUrl.maxLength = 2000;
    inUrl.placeholder = 'https://';
    inUrl.setAttribute('aria-describedby', urlHintId);
    var urlHint = el('p', 'wcn-hint', 'Paste the https link of an image or video from your WowStore media (a WowStore address). Other links are refused.');
    urlHint.id = urlHintId;
    fUrl.appendChild(lUrl);
    fUrl.appendChild(inUrl);
    fUrl.appendChild(urlHint);
    form.appendChild(fUrl);

    /* media type */
    var fType = el('div', 'wcn-field');
    var typeId = nextId('type');
    var lType = el('label', 'wcn-label', 'Media type');
    lType.htmlFor = typeId;
    var selType = el('select', 'wcn-input');
    selType.id = typeId;
    [['image', 'Image'], ['video', 'Video']].forEach(function (p) {
      var o = el('option', null, p[1]);
      o.value = p[0];
      selType.appendChild(o);
    });
    fType.appendChild(lType);
    fType.appendChild(selType);
    form.appendChild(fType);

    /* caption */
    var fCap = el('div', 'wcn-field');
    var capId = nextId('cap'), cntId = nextId('cnt');
    var lCap = el('label', 'wcn-label', 'Caption');
    lCap.htmlFor = capId;
    var ta = el('textarea', 'wcn-input');
    ta.id = capId;
    ta.maxLength = CAPTION_MAX;
    ta.rows = 5;
    ta.setAttribute('aria-describedby', cntId);
    var counter = el('p', 'wcn-counter', '0 / 2,200');
    counter.id = cntId;
    fCap.appendChild(lCap);
    fCap.appendChild(ta);
    fCap.appendChild(counter);
    form.appendChild(fCap);

    /* preview */
    var prev = el('div', 'wcn-preview');
    prev.setAttribute('aria-label', 'Preview of your post');
    prev.appendChild(el('p', 'wcn-preview-head', 'Preview'));
    var prevAcct = el('p', 'wcn-preview-acct', '');
    var prevMedia = el('div', 'wcn-preview-mediawrap');
    var prevCap = el('p', 'wcn-preview-caption', '');
    prev.appendChild(prevAcct);
    prev.appendChild(prevMedia);
    prev.appendChild(prevCap);
    form.appendChild(prev);

    /* approval: unchecked by default, resets whenever the post changes */
    var apId = nextId('approve'), apHintId = nextId('aphint');
    var apWrap = el('label', 'wcn-approve');
    apWrap.htmlFor = apId;
    var ap = el('input', 'wcn-check');
    ap.type = 'checkbox';
    ap.id = apId;
    ap.checked = false;
    ap.setAttribute('aria-describedby', apHintId);
    apWrap.appendChild(ap);
    apWrap.appendChild(el('span', null, 'I approve this post to my account'));
    form.appendChild(apWrap);
    var apHint = el('p', 'wcn-hint', 'Nothing is posted without your approval. If you change the post, tick the box again.');
    apHint.id = apHintId;
    form.appendChild(apHint);

    var actions = el('div', 'wcn-actions');
    var pub = el('button', 'wcn-btn wcn-btn-primary', 'Publish');
    pub.type = 'submit';
    pub.disabled = true;
    actions.appendChild(pub);
    form.appendChild(actions);

    var result = el('p', 'wcn-note');
    result.hidden = true;
    form.appendChild(result);

    var lastPreviewKey = '';
    /* one idempotency key per post CONTENT: a re-send of the same content re-uses it, so it can never post twice */
    var idemKey = '', idemContent = '';
    /* content of a job that is open or could not be confirmed: the form stays shut for exactly this content */
    var blockedContent = '';

    function currentUrl() { return safeHttps(inUrl.value.trim()); }

    function contentKey() {
      var a = accounts[Number(sel.value)] || {};
      return [a.id, a.kind, currentUrl() || '', selType.value, ta.value].join('\u0001');
    }

    function updatePreview() {
      var a = accounts[Number(sel.value)] || accounts[0];
      prevAcct.textContent = (a.name || a.username || 'Account') + ' \u00b7 ' + kindLabel(a.kind);
      prevCap.textContent = ta.value || 'Your caption appears here.';
      var u = currentUrl();
      var key = (u || '') + '|' + selType.value;
      if (key === lastPreviewKey) return;
      lastPreviewKey = key;
      clear(prevMedia);
      if (!u) return;
      var m;
      if (selType.value === 'video') {
        m = el('video', 'wcn-preview-media');
        m.controls = true;
        m.muted = true;
        m.preload = 'metadata';
        m.setAttribute('playsinline', '');
      } else {
        m = el('img', 'wcn-preview-media');
        m.alt = 'Preview of the media you linked';
        m.referrerPolicy = 'no-referrer';
      }
      m.addEventListener('error', function () {
        clear(prevMedia);
        prevMedia.appendChild(el('p', 'wcn-hint wcn-hint-err', 'We could not load a preview from this link. Check that it opens the file directly.'));
      });
      m.src = u;
      prevMedia.appendChild(m);
    }

    function updateState() {
      var raw = inUrl.value.trim();
      var u = currentUrl();
      if (raw && !u) {
        urlHint.textContent = 'The link must start with https://';
        urlHint.className = 'wcn-hint wcn-hint-err';
        inUrl.setAttribute('aria-invalid', 'true');
      } else {
        urlHint.textContent = 'Paste the https link of an image or video from your WowStore media (a WowStore address). Other links are refused.';
        urlHint.className = 'wcn-hint';
        inUrl.removeAttribute('aria-invalid');
      }
      counter.textContent = ta.value.length.toLocaleString('en-GB') + ' / 2,200';
      var blocked = !!blockedContent && blockedContent === contentKey();
      pub.disabled = publishing || blocked || !u || !ap.checked || ta.value.length > CAPTION_MAX;
      sel.disabled = inUrl.disabled = selType.disabled = ta.disabled = ap.disabled = publishing;
    }

    function postChanged() {
      ap.checked = false;
      result.hidden = true;
      updatePreview();
      updateState();
    }

    sel.addEventListener('change', postChanged);
    selType.addEventListener('change', postChanged);
    ta.addEventListener('input', postChanged);
    inUrl.addEventListener('input', function () {
      var g = guessMediaType(inUrl.value.trim());
      if (g) selType.value = g;
      postChanged();
    });
    ap.addEventListener('change', updateState);

    function showResult(text, link) {
      clear(result);
      result.appendChild(document.createTextNode(text));
      var safe = safeHttps(link);
      if (safe) {
        var aEl = el('a', 'wcn-link', 'View the post');
        aEl.href = safe;
        aEl.target = '_blank';
        aEl.rel = 'noopener noreferrer';
        result.appendChild(document.createTextNode(' '));
        result.appendChild(aEl);
      }
      result.hidden = false;
    }

    /* every way a publish attempt ends goes through here. retryable=true ONLY when we know nothing was posted */
    function settle(msg, opts) {
      opts = opts || {};
      if (jobTimer) { clearInterval(jobTimer); jobTimer = null; }
      publishing = false;
      pub.textContent = 'Publish';
      ap.checked = false;
      if (opts.retryable) { idemKey = ''; idemContent = ''; blockedContent = ''; }
      if (opts.block) blockedContent = contentKey();
      if (opts.published) {
        idemKey = ''; idemContent = ''; blockedContent = '';
        inUrl.value = '';
        ta.value = '';
        lastPreviewKey = '';
        clear(prevMedia);
        updatePreview();
      }
      updateState();
      showResult(msg, opts.link);
      say(msg);
      loadPosts();
    }

    function onJob(job) {
      /* returns true when the job reached an end state */
      var st = job && job.status;
      if (st === 'published') { settle('Published.', { published: true, link: job.permalink }); return true; }
      if (st === 'failed') { settle(shortText(job.error || 'The post was not published.', 200) + ' Nothing was posted. You can try again.', { retryable: true }); return true; }
      if (st === 'cancelled') { settle('This post was stopped because the account was disconnected. Nothing was posted.', { retryable: true }); return true; }
      if (st === 'unknown') { showResult(MSG_UNKNOWN); return false; }     /* keep polling: the server reconciles it */
      return false;
    }

    function trackJob(jobId, my) {
      var tries = 0, busy = false, sawUnknown = false;
      if (jobTimer) clearInterval(jobTimer);
      jobTimer = setInterval(function () {
        if (my !== epoch) { clearInterval(jobTimer); jobTimer = null; return; }
        if (busy) return;
        tries += 1;
        if (tries > JOB_POLL_MAX) {
          settle(sawUnknown ? MSG_UNKNOWN : MSG_STILL, { block: true });
          return;
        }
        busy = true;
        api('GET', '/posts/' + encodeURIComponent(jobId)).then(function (res) {
          busy = false;
          if (my !== epoch) return;
          if (res.status === 401) { settle('Your login has expired. Log in again, then check your account before you post again.', { block: true }); return; }
          if (res.status === 404) { settle(MSG_STILL, { block: true }); return; }
          if (!res.ok || !res.data) return;                                 /* a hiccup: next tick */
          if (res.data.status === 'unknown' && !sawUnknown) { sawUnknown = true; say(MSG_UNKNOWN); }
          onJob(res.data);
        }).catch(function () { busy = false; });
      }, JOB_POLL_MS);
    }

    function sendPublish(payload, my, attempt) {
      api('POST', '/publish', payload, 20000).then(function (res) {
        if (my !== epoch) return;
        var d = res.data;
        if (res.ok && d && d.job_id) {
          if (!onJob(d)) trackJob(d.job_id, my);
          return;
        }
        if (res.status === 401) { settle('Your login has expired. Log in again.', {}); return; }
        if (res.status === 409) { settle(serverDetail(res, MSG_STILL), { block: true }); return; }
        if (res.status >= 400 && res.status < 500 && d) {
          /* refused before anything was queued */
          settle(serverDetail(res, 'The post was not accepted. Please check the link.'), { retryable: true });
          return;
        }
        throw new Error('no-answer');                                       /* 5xx, 504 page, empty body */
      }).catch(function () {
        if (my !== epoch) return;
        if (attempt < PUBLISH_SEND_TRIES) {
          setTimeout(function () { if (my === epoch) sendPublish(payload, my, attempt + 1); }, PUBLISH_SEND_GAP_MS);
          return;
        }
        /* same content => same key => pressing Publish again asks about the SAME job, it cannot post twice */
        settle('We could not reach the server, so this post is not confirmed. Press Publish again to check on it: it will not be posted twice.', {});
      });
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var u = currentUrl();
      if (publishing || !u || ap.checked !== true) return;
      var a = accounts[Number(sel.value)];
      if (!a) return;
      var ck = contentKey();
      if (blockedContent && blockedContent === ck) return;
      if (!idemKey || idemContent !== ck) { idemKey = newKey(); idemContent = ck; }
      publishing = true;
      result.hidden = true;
      updateState();
      pub.textContent = 'Publishing\u2026';
      say('Publishing\u2026 this can take a few minutes for a video. You can leave this page open.');
      sendPublish({
        account_id: a.id,
        kind: a.kind,
        media_url: u,
        media_type: selType.value === 'video' ? 'video' : 'image',
        caption: ta.value,
        approved: true,
        idempotency_key: idemKey
      }, epoch, 1);
    });

    bodyEl.appendChild(form);
    updatePreview();
    updateState();
  }

  /* ------------------------------------------------------------------ results */

  var postsBox = null;

  function renderPosts() {
    bodyEl.appendChild(el('h3', 'wcn-sub', 'Your posts'));
    postsBox = el('div', 'wcn-postsbox');
    postsBox.appendChild(el('p', 'wcn-note', 'Loading your posts.'));
    bodyEl.appendChild(postsBox);
    loadPosts();
  }

  function metric(dl, label, value) {
    var w = el('div', 'wcn-metric');
    w.appendChild(el('dt', null, label));
    w.appendChild(el('dd', null, fmtNum(value)));
    dl.appendChild(w);
  }

  function loadPosts() {
    var box = postsBox;
    if (!box) return;
    var my = epoch;
    api('GET', '/posts').then(function (res) {
      if (my !== epoch || box !== postsBox) return;
      clear(box);
      var posts = res.ok && res.data && Array.isArray(res.data.posts) ? res.data.posts : null;
      if (!posts) {
        box.appendChild(el('p', 'wcn-note', 'Results are not available right now.'));
        return;
      }
      var pending = Number(res.data.pending) || 0, unknown = Number(res.data.unknown) || 0;
      if (pending > 0) {
        box.appendChild(el('p', 'wcn-note', (pending === 1 ? '1 post is' : pending + ' posts are') + ' being published. Do not post the same file again.'));
      }
      if (unknown > 0) {
        box.appendChild(el('p', 'wcn-note', (unknown === 1 ? '1 post' : unknown + ' posts') + ' could not be confirmed. Check your account before you post the same file again.'));
      }
      if (postsTimer) { clearTimeout(postsTimer); postsTimer = null; }
      if ((pending > 0 || unknown > 0) && !jobTimer && postsRefreshes < POSTS_REFRESH_MAX) {
        postsRefreshes += 1;
        postsTimer = setTimeout(function () { postsTimer = null; if (my === epoch) loadPosts(); }, POSTS_REFRESH_MS);
      } else if (pending === 0 && unknown === 0) {
        postsRefreshes = 0;
      }
      if (!posts.length && (pending > 0 || unknown > 0)) return;
      if (!posts.length) {
        box.appendChild(el('p', 'wcn-note', 'No posts yet. Posts you publish from here appear in this list.'));
        return;
      }
      var ul = el('ul', 'wcn-posts');
      posts.slice(0, 50).forEach(function (p) {
        var li = el('li', 'wcn-post');
        li.appendChild(el('p', 'wcn-post-caption', shortText(p.caption, 90) || 'No caption'));
        li.appendChild(el('p', 'wcn-post-meta', fmtDate(p.created_at) + ' \u00b7 ' + kindLabel(p.kind) + (p.account_name ? ' \u00b7 ' + p.account_name : '')));
        var m = p.metrics && typeof p.metrics === 'object' ? p.metrics : {};
        var dl = el('dl', 'wcn-metrics');
        metric(dl, 'Likes', m.likes);
        metric(dl, 'Comments', m.comments);
        metric(dl, 'Reach', m.reach);
        metric(dl, 'Views', m.views);
        li.appendChild(dl);
        var link = safeHttps(p.permalink);
        if (link) {
          var a = el('a', 'wcn-link', 'View the post');
          a.href = link;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          li.appendChild(a);
        }
        ul.appendChild(li);
      });
      box.appendChild(ul);
    }).catch(function () {
      if (my !== epoch || box !== postsBox) return;
      clear(box);
      box.appendChild(el('p', 'wcn-note', 'Results are not available right now.'));
    });
  }

  /* ------------------------------------------------------------------ boot */

  if (typeof MutationObserver === 'function') {
    new MutationObserver(sync).observe(dash, { attributes: true, attributeFilter: ['class'] });
  }
  /* another tab logged out: drop the card here too */
  window.addEventListener('storage', function (ev) {
    if (ev.key === SESSION_KEY || ev.key === ID_KEY || ev.key === null) sync();
  });
  sync();
})();

